import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { useControlledValue } from "../../hooks/_private/useControlledValue";
import { Suggestions } from './Suggestions';
import { Actionable } from "../actionable";
import { Hidden } from "../hidden";
import { Text } from "../text";
import { TextFieldBase } from "../text-field/TextFieldBase";
import { Icon } from "../icon";
import { Search } from '@az/starc-ui-icons';
import { View } from "../view";
import { useElementId } from "../../hooks/useElementId";
import { classNames } from "../../utils/helpers";
import s from './SearchBar.module.css';
export const SearchBar = React.forwardRef(({ suggestions, defaultValue, value, onValueChange, onSelectionChange, onValueClear, disabled, hideSuggestions, maxMenuHeight, resultsHeaderText = 'Most Popular Searches', narrowSearchText = 'Narrow Search', clearQueryText = 'clear', onNarrowSearchClick, className, suggestionsOffset = 0, label, name, id, inputAttributes, variant = 'default', endElement, startElement, suggestionsClassName, textFieldRootClassName, inputClassName, inputContainerClassName, }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    // When a suggestion was just selected or 'Esc' was pressed, we want to hide the suggestions list
    // This should be reset when the input is focused again or the input value changes
    const [suggestionWasHidden, setSuggestionWasHidden] = React.useState(false);
    const [controlledValue, setControlledValue] = useControlledValue({
        defaultValue,
        value,
        onValueChange,
    }, defaultValue !== null && defaultValue !== void 0 ? defaultValue : '');
    const [suggestionValue, setSuggestionValue] = React.useState('');
    const [isSuggestion, setIsSuggestion] = React.useState(false);
    const containerClassNames = classNames(className, variant && s[`--variant-${variant}`], s.root);
    const inputId = useElementId(id);
    const inputRef = React.useRef(ref && typeof ref !== 'function' ? ref.current : null);
    const searchRef = React.useRef(ref && typeof ref !== 'function' ? ref.current : null);
    const suggestionsShown = !hideSuggestions && !suggestionWasHidden && isFocused;
    const updatedAttributes = Object.assign(Object.assign({}, inputAttributes), { tabIndex: 0, role: 'combobox', autoComplete: 'off', 'aria-expanded': suggestionsShown, 'aria-owns': 'suggestions', onFocus: (e) => {
            handleFocus(e);
        }, onBlur: (e) => {
            handleBlur(e);
        }, placeholder: label, style: { fontSize: 'var(--st-font-size-100)' } });
    // event handlers
    const handleBlur = (e) => {
        var _a;
        (inputAttributes === null || inputAttributes === void 0 ? void 0 : inputAttributes.onBlur) && ((_a = inputAttributes.onBlur) === null || _a === void 0 ? void 0 : _a.call(inputAttributes, e));
    };
    const handleChange = (e) => {
        var _a;
        e.preventDefault();
        const { value } = e.target;
        setControlledValue(value);
        setSuggestionWasHidden(false);
        (inputAttributes === null || inputAttributes === void 0 ? void 0 : inputAttributes.onChange) && ((_a = inputAttributes.onChange) === null || _a === void 0 ? void 0 : _a.call(inputAttributes, e));
    };
    const handleFocus = (e) => {
        var _a;
        (inputAttributes === null || inputAttributes === void 0 ? void 0 : inputAttributes.onFocus) && ((_a = inputAttributes.onFocus) === null || _a === void 0 ? void 0 : _a.call(inputAttributes, e));
        setIsFocused(true);
        setSuggestionWasHidden(false);
    };
    // Render functions
    const renderClearButton = () => {
        if (controlledValue === '') {
            return undefined;
        }
        else {
            return (_jsx(Actionable
            // FIXME: https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/93f78856655696a55309440593e0948c6fb96134/docs/rules/tabindex-no-positive.md
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            , { 
                // FIXME: https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/93f78856655696a55309440593e0948c6fb96134/docs/rules/tabindex-no-positive.md
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex: 1, attributes: { 'aria-label': 'clear search', id: 'search-clear-button' }, ref: searchRef, onClick: () => {
                    setControlledValue('');
                    onValueClear === null || onValueClear === void 0 ? void 0 : onValueClear();
                    const input = document.getElementById(inputId);
                    input === null || input === void 0 ? void 0 : input.focus();
                }, children: _jsx(Text, { color: "on-disabled", children: clearQueryText }) }));
        }
    };
    const renderSearchIcon = () => {
        return (_jsx(Icon, { svg: Search, size: 8, attributes: { style: { marginRight: 'var(--st-unit-2)' } } }));
    };
    const renderSuggestions = () => {
        if (suggestionsShown && React.isValidElement(suggestions)) {
            return (_jsx(_Fragment, { children: React.cloneElement(suggestions, {
                    inputId,
                    setSuggestionWasHidden,
                    onSelectionChange: (value) => {
                        setSuggestionWasHidden(true);
                        setControlledValue(value);
                        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(value);
                    },
                }) }));
        }
        else if (suggestionsShown &&
            suggestions &&
            Array.isArray(suggestions) &&
            suggestions.length > 0) {
            return (_jsx(Suggestions, { suggestionsClassName: suggestionsClassName, inputId: inputId, searchInputRef: inputRef, searchRef: searchRef, inputValue: controlledValue, suggestions: suggestions, offset: suggestionsOffset, setSuggestionValue: setSuggestionValue, setIsSuggestion: setIsSuggestion, onTabEvent: setIsFocused, onSelectionChange: (value, shouldRemoveModal) => {
                    if (shouldRemoveModal) {
                        setSuggestionWasHidden(true);
                        setControlledValue(value);
                        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(value);
                    }
                    else {
                        setControlledValue(value);
                        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(value);
                    }
                }, onHideSuggestions: () => {
                    setSuggestionWasHidden(true);
                }, resultsHeaderText: resultsHeaderText, narrowSearchText: narrowSearchText, onNarrowSearchClick: onNarrowSearchClick, maxMenuHeight: maxMenuHeight }));
        }
        return null;
    };
    return (_jsxs(View, { className: classNames(containerClassNames), attributes: { 'data-testid': 'st-search-bar' }, children: [_jsx(TextFieldBase, { ref: inputRef, id: inputId, name: name, label: "", className: classNames(s.fieldRoot, textFieldRootClassName), inputClassName: classNames(s.directInput, inputClassName), inputContainerClassName: classNames(s.inputContainer, inputContainerClassName), value: isSuggestion ? suggestionValue : controlledValue, onChange: handleChange, inputAttributes: updatedAttributes, disabled: disabled, endElement: endElement || renderClearButton(), startElement: startElement || renderSearchIcon() }), _jsx(Hidden, { hide: !suggestionsShown, children: className => (_jsx(View, { className: classNames(s.suggestions, className), backgroundColor: hideSuggestions ? undefined : 'secondary', attributes: {
                        role: 'listbox',
                        'aria-label': 'suggestions',
                        'aria-hidden': !suggestionsShown,
                        id: 'suggestions',
                        tabIndex: 0,
                    }, children: _jsx(_Fragment, { children: renderSuggestions() }) })) })] }));
});
SearchBar.displayName = 'SearchBar';
